import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogRatioCreateInput } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { createCatalogRatioGql } from '../catalogRatiosQueries';

const createCatalogRatioItemRequestId = 'createCatalogRatio';

export const useCreateRatio = ({
    closeModalHandler,
    refreshData,
}: {
    closeModalHandler: () => void;
    refreshData: () => void;
}) => {
    const dispatch = useDispatch();

    const [resetCreateData] = useResetStoreData({ requestId: createCatalogRatioItemRequestId });
    const [validationMessages, setValidationMessages] = useState<Record<string, string | null>>({});

    const tempSuccessHandler = useCallback(() => {
        closeModalHandler();
        refreshData();
        resetCreateData(undefined);
    }, [closeModalHandler, resetCreateData, refreshData]);

    const [createRatioData, createRatioStatus, , createRatioRequest] = useRequest({
        requestId: createCatalogRatioItemRequestId,
        query: createCatalogRatioGql,
        withEffect: false,
        dependenciesUpdater: tempSuccessHandler,
    });

    useEffect(() => {
        if (createRatioData?.createCatalogRatio?.validation?.length) {
            const validationState = createRatioData?.createCatalogRatio?.validation.reduce(
                (acc, v) => ({
                    ...acc,
                    [v.property ?? 'unknown']: v.message,
                }),
                {},
            );

            setValidationMessages(validationState);
        } else {
            setValidationMessages({});
        }
    }, [createRatioData?.createCatalogRatio?.validation]);

    const createRatioHandler = useCallback(
        (input: CatalogRatioCreateInput) => {
            dispatch(createRatioRequest({ input }));
        },
        [createRatioRequest, dispatch],
    );

    return useMemo(
        () => ({
            createRatioStatus,
            createRatioHandler,
            resetCreateData,
            validationMessages,
        }),
        [createRatioHandler, createRatioStatus, resetCreateData, validationMessages],
    );
};
